import React, { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { SearchScreen } from 'pages/search/SearchScreen'
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from 'react-router-dom'
import { IndexProductScreen } from 'IndexProductScreen'
import { IndexDrawingScreen } from 'IndexDrawingScreen'
import { NavBar } from 'NavBar'
import { ManageUsersScreen } from 'ManageUsersScreen'
import { SettingsScreen } from 'pages/settings/SettingsScreen'
import { ActiveTab, ManageProductsScreen } from 'pages/manage/products/ManageProductsScreen'
import { TestPDFScreen } from 'pages/TestPDFScreen'
import { isAnyAdmin, isMerchant } from 'models/User'
import { User } from './hooks/useUsers'

import { PAID_ADD_ONS } from 'constants/Constants'
import config from 'config'

// @ts-ignore
import JKHLogo2 from '../public/images/JKH-Logo-transparent-bg.png'

// @ts-ignore
import TeamImage from '../public/images/undraw_team_landing_page.svg'
import { useManagedProductCounts } from 'hooks/useManagedProductCounts'
import { ViewProductVersionScreen } from 'pages/product-version/ViewProductVersionScreen'
import { useAuth0Authentication } from 'hooks/useAuth0Authentication'

import { User as Auth0User } from '@auth0/auth0-spa-js';

export const Permissions = {
    canAdd: (user: User) => {
        if (PAID_ADD_ONS.quarantineZone.isEnabled) {
            return isAnyAdmin(user) && !isMerchant(user)
        }

        return isAnyAdmin(user) && !isMerchant(user)
    },
    canEdit: (user: User) => {
        if (PAID_ADD_ONS.quarantineZone.isEnabled) {
            return isAnyAdmin(user) && !isMerchant(user)
        }

        return isAnyAdmin(user) && !isMerchant(user)
    },
    canDelete: (user: User) => {
        return isAnyAdmin(user)
    },
    canViewProductHistory: (user: User) => {
        if (PAID_ADD_ONS.quarantineZone.isEnabled) {
            return isAnyAdmin(user)&& !isMerchant(user)
        }

        return false
    },
    canInitateChangeRequest: (user: User) => {
        if (PAID_ADD_ONS.quarantineZone.isEnabled) {
            return isAnyAdmin(user) && !isMerchant(user)
        }

        return false
    },
    canViewManaged: (user: User) => {
        if (PAID_ADD_ONS.quarantineZone.isEnabled) {
            return isAnyAdmin(user) && !isMerchant(user)
        }

        return false 
    },
    canViewAllManaged: (user: User) => {
        if (PAID_ADD_ONS.quarantineZone.isEnabled) {
            return isAnyAdmin(user) && !isMerchant(user)
        }

        return false
    },
    canViewManageUsers: (user: User) => {
        return isAnyAdmin(user) && !isMerchant(user)
    },
    canViewSettings: (user: User) => {
        return isAnyAdmin(user) && !isMerchant(user)
    },
    canDownloadProductionChecklist: (user: User) => {
        if (PAID_ADD_ONS.manufacturing.isEnabled) {
            return !isMerchant(user)
        }

        return false
    }
}

// Forces page to scroll to top when we navigate via the router.
const ScrollToTop: React.FC = () => {
    const { pathname } = useLocation()

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [pathname])

    return null
}

const AuthenticatedApp = ({ user }: { user: Auth0User }) => {
    
    const { getAuth0AccessToken } = useAuth0Authentication()
    const [loggedInUser, setLoggedInUser] = useState<User>(undefined)
    const { manageProductCounts, refresh: refreshCounts } = useManagedProductCounts()

    useEffect(() => {

        const getLoggedInUser = async () => {
            const accessToken: string = await getAuth0AccessToken()

            const getUsersResponse: Promise<Response> = fetch(`${config.apiUrl}/users/${encodeURI(user.sub)}`, {
                method: 'GET',
                redirect: 'follow',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": 'application/json'
                },
            })

            const response: Response = await getUsersResponse
            const userResponse: User = await response.json()

            setLoggedInUser(userResponse)
        }

        user?.sub && getLoggedInUser()
            
    }, [user])

    if (!loggedInUser) return null;

    return (
        <Router>
            <ScrollToTop/>
            <NavBar loggedInUser={loggedInUser} manageProductCounts={manageProductCounts} />
            <Routes>
                {Permissions.canAdd(loggedInUser) && <Route path="/add/product"  element={<IndexProductScreen loggedInUser={loggedInUser} />} /> }
                {Permissions.canAdd(loggedInUser) && <Route path="/add/drawing"  element={<IndexDrawingScreen loggedInUser={loggedInUser} />} /> }
                {Permissions.canViewAllManaged(loggedInUser) && <Route path="/manage/products" element={<ManageProductsScreen loggedInUser={loggedInUser} activeTab={ActiveTab.PENDING_REVIEW} manageProductCounts={manageProductCounts} refreshCounts={refreshCounts} />} /> }
                {Permissions.canViewAllManaged(loggedInUser) && <Route path="/manage/products/edits" element={<ManageProductsScreen loggedInUser={loggedInUser} activeTab={ActiveTab.EDITS_REQUESTED} manageProductCounts={manageProductCounts} refreshCounts={refreshCounts} />} /> }
                {Permissions.canViewAllManaged(loggedInUser) && <Route path="/manage/products/archived" element={<ManageProductsScreen loggedInUser={loggedInUser} activeTab={ActiveTab.ARCHIVED} manageProductCounts={manageProductCounts} refreshCounts={refreshCounts} />} /> }
                <Route path="/view/product/:productCode/versions/:version" element={<ViewProductVersionScreen loggedInUser={loggedInUser} />} />
                <Route path="/edit/product/:productCode" element={<IndexProductScreen pageState={Permissions.canEdit(loggedInUser) ? 'edit' : 'view'} loggedInUser={loggedInUser} />} />
                {!isMerchant(loggedInUser) && <Route path="/edit/drawing/:objectID" element={<IndexDrawingScreen pageState={Permissions.canEdit(loggedInUser) ? 'edit' : 'view'} loggedInUser={loggedInUser} />} /> }
                {/** Used for linking to drawing from product temporily. */}
                {!isMerchant(loggedInUser) && <Route path="/view/drawing/:objectID" element={<IndexDrawingScreen pageState={'view'} loggedInUser={loggedInUser} />} /> }
                {Permissions.canViewManageUsers(loggedInUser) && <Route path="/manage-users" element={<ManageUsersScreen loggedInUser={loggedInUser} />} /> }
                {Permissions.canViewSettings(loggedInUser) && <Route path="/settings" element={<SettingsScreen />} /> }
                <Route path="/pdf-test" element={<TestPDFScreen />} />
                <Route path="/search/:index" element={<SearchScreen loggedInUser={loggedInUser} />} />
                <Route path="/" element={<Navigate replace to={'/search/products'} />} />
                <Route path="*" element={<Navigate replace to={'/'} />} />
            </Routes>
        </Router>
    )   
}

const AppController = () => {

    const { user, isAuthenticated, isLoading } = useAuth0()
    const { handleLogin } = useAuth0Authentication()

    // Wait for Auth0 to finish initialising
    // before making any decisions
    if (isLoading) return null

    // Not authenticated — show login screen
    // Do NOT attempt any token fetch here
    if (!isAuthenticated) return (
        <section className="hero is-fullheight">
            <div className="hero-body">

                <nav className="navbar is-fixed-top mt-2" style={{background: '#f9f9f9'}}>
                    <div className="container">
                        <div className="navbar-brand">
                            <span className="subtitle is-2">cadly</span>
                        </div>
                    </div>
                </nav>

                <div className="container has-text-centered">
                    <div className="columns is-vcentered">
                        <div className="column is-5">
                            <img src={JKHLogo2} alt="JKH logo." width={'30%'} height={'30%'} />
                            <h1 className="title is-1">👋🏻 Welcome back!</h1>
                            <h2 className="subtitle is-3">Log in to join your team</h2>
                            <button className="button is-link is-rounded is-medium" onClick={() => handleLogin()}>Log In</button>
                        </div>
                        <div className="column is-5 is-offset-1">
                            <img src={TeamImage} alt="An image of a team." />
                        </div>
                    </div>

                </div>
            </div>
        </section>
    )

    // Only reach here if genuinely authenticated
    return <AuthenticatedApp user={user} />
}

export { AppController }